import React, { useState, useEffect, useRef, useCallback } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import { Button } from "@/components/ui/button";
import {
    Bell,
    BellOff,
    CheckCheck,
    Trash2,
    Clock,
    MailOpen,
} from "lucide-react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { setNotificationCount } from "@/store/feature/UserSlice";
import type { RootState } from "@/store/store";
import type { INotification } from "@/interface/common.interface";
import { NotificationService } from "@/services/Notification/NotificationService";
import { ThrowError } from "@/api/callApi";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import { showToastNotification } from "@/utility/helper";

const PAGE_SIZE = 10;

const Notification: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentUser = useSelector((state: RootState) => state.user.user);
    const notificationCount = useSelector(
        (state: RootState) => state.user.notificationCount,
    );

    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [activeTab, setActiveTab] = useState<"all" | "unread" | "read">("all");

    const parentRef = useRef<HTMLDivElement>(null);
    const isFetchingRef = useRef(false);
    const currentPageRef = useRef(1);

    const fetchNotifications = useCallback(
        async (pageNumber: number, reset: boolean) => {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;
            currentPageRef.current = pageNumber;

            if (reset) {
                setIsInitialLoading(true);
            } else {
                setIsFetchingMore(true);
            }

            let seen: boolean | undefined;
            if (activeTab === "unread") seen = false;
            else if (activeTab === "read") seen = true;

            try {
                const response = await NotificationService.getList({
                    pageNumber,
                    pageSize: PAGE_SIZE,
                    seen,
                });

                const list = response?.data?.notifications ?? [];
                const totalPages = response?.data?.pageInfo?.totalPages ?? 1;

                setNotifications((prev) => {
                    if (reset) return list;
                    // Deduplicate by ID to prevent any duplicate rendering or layout shifts
                    const existingIds = new Set(prev.map((item) => item.id));
                    const newUnique = list.filter((item) => !existingIds.has(item.id));
                    return [...prev, ...newUnique];
                });
                setHasMore(pageNumber < totalPages);
            } catch (error) {
                console.error("Failed to fetch notifications:", error);
            } finally {
                isFetchingRef.current = false;
                setIsInitialLoading(false);
                setIsFetchingMore(false);
            }
        },
        [activeTab],
    );

    // Initial load & Tab switch handler
    useEffect(() => {
        currentPageRef.current = 1;
        setHasMore(true);
        fetchNotifications(1, true);
        parentRef.current?.scrollTo({ top: 0 });
    }, [activeTab]);

    // ── TanStack Virtualizer Setup ─────────────────────────────────────────
    const rowVirtualizer = useVirtualizer({
        count: hasMore ? notifications.length + 1 : notifications.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 88, // estimated dynamic height of a notification item
        overscan: 5, // Render 5 buffer items above/below viewport for lag-free 60fps scrolling
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    // ── Trigger next page when near bottom of virtual scroll ────────────────
    useEffect(() => {
        const lastItem = virtualItems[virtualItems.length - 1];
        if (!lastItem) return;

        if (
            lastItem.index >= notifications.length - 1 &&
            hasMore &&
            !isFetchingRef.current &&
            !isInitialLoading
        ) {
            const nextPage = currentPageRef.current + 1;
            fetchNotifications(nextPage, false);
        }
    }, [virtualItems, hasMore, notifications.length, isInitialLoading, fetchNotifications]);

    const handleMarkSingleAsSeen = async (id: string) => {
        try {
            await NotificationService.markAsSeen([id], true);
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, seen: true } : n)),
            );
            dispatch(setNotificationCount(Math.max(notificationCount - 1, 0)));
        } catch (error) {
            ThrowError(error);
        }
    };

    const handleMarkAllAsRead = async () => {
        const unseenIds = notifications.filter((n) => !n.seen).map((n) => n.id);
        if (unseenIds.length === 0) return;

        try {
            await NotificationService.markAsSeen(unseenIds, true);
            setNotifications((prev) => prev.map((n) => ({ ...n, seen: true })));
            dispatch(setNotificationCount(0));
        } catch (error) {
            ThrowError(error);
        }
    };

    const handleDeleteSingle = async (id: string, wasUnseen: boolean) => {
        try {
            await NotificationService.delete([id]); // Pass as array
            setNotifications((prev) => prev.filter((n) => n.id !== id));
            if (wasUnseen) {
                dispatch(setNotificationCount(Math.max(notificationCount - 1, 0)));
            }
        } catch (error) {
            ThrowError(error);
        }
    };

    const handleClearAll = async () => {
        const ids = notifications.map((n) => n.id);
        if (ids.length === 0) return;

        try {
            await NotificationService.delete(ids); // Bulk delete
            setNotifications([]);
            dispatch(setNotificationCount(0));
        } catch (error) {
            ThrowError(error);
        }
    };

    const handleNotificationClick = (notification: INotification) => {
        // If unread, mark as seen
        if (!notification.seen) {
            handleMarkSingleAsSeen(notification.id);
        }

        const rawType = notification?.data?.type || "";
        const type = String(rawType).toLowerCase().trim();
        const data = notification?.data;

        if (!data) return;

        const userLevel = currentUser?.designation?.level;
        const isLevelAdmin =
            userLevel !== undefined &&
            userLevel !== null &&
            (Number(userLevel) === 0 || Number(userLevel) === 1);
        const isAdmin =
            currentUser?.type === "admin" || currentUser?.type === "super_admin";
        const canAccessTeam =
            Boolean(currentUser?.designation?.canAccessTeam) ||
            isLevelAdmin ||
            isAdmin;

        // Extract relevant ID based on type or fallbacks
        const targetId =
            data.leaveId ||
            data.wfhId ||
            data.outdoorDutyId ||
            (data as any).regularizationId ||
            (data as any).id ||
            "";

        if (!targetId) return;
        const Id = data.userId ? data?.userId : targetId;

        // const isOwnRequest = Boolean(
        //     data.userId && currentUser?.id && data.userId === currentUser.id
        // );

        if (type === "leave") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamLeaveDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.leaveDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team leave requests."
                );
            }
        } else if (type === "wfh") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamWfhDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.wfhDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team WFH requests."
                );
            }
        } else if (type === "outdoor-duty" || type === "outdoor_duty" || type === "od") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamOdDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.odDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team OD requests."
                );
            }
        } else if (type === "regularization") {
            if (canAccessTeam) {
                navigate(`${RoutesLink.teamsRegularizationDetail}/${targetId}`);
            } else if (!data?.userId) {
                navigate(`${RoutesLink.regularizationDetail}/${Id}`);
            } else {
                showToastNotification(
                    "danger",
                    "You do not have permission to access team regularization requests."
                );
            }
        } else if (data.userId) {
            navigate(`${RoutesLink.employeeDetail}/${data.userId}`);
        }
    };

    const formatDate = (date: string | Date) =>
        moment(date).format("DD MMM YYYY, hh:mm A");

    const isBusy = isInitialLoading || isFetchingMore;

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <MainHeading title="Notifications" isBackButtonVisible={true} />

                    {notifications.length > 0 && (
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={handleMarkAllAsRead}
                                disabled={isBusy}
                                className="h-10 text-xs font-semibold hover:bg-slate-50 transition-colors"
                            >
                                <CheckCheck size={16} className="mr-2 text-primary" />
                                Mark all as read
                            </Button>
                            <Button
                                variant="ghost"
                                onClick={handleClearAll}
                                disabled={isBusy}
                                className="h-10 text-xs font-semibold text-red-600 hover:text-red-700 hover:bg-red-50/50 transition-colors"
                            >
                                <Trash2 size={16} className="mr-2 text-red-500" />
                                Clear all
                            </Button>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    {(["all", "unread", "read"] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => {
                                setActiveTab(tab);
                            }}
                            className={`px-6 py-3.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-all relative cursor-pointer
                ${activeTab === tab
                                    ? "border-primary text-primary font-bold"
                                    : "border-transparent text-gray-400 hover:text-gray-600 font-semibold"
                                }
              `}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* Scrollable Virtualized Notification List */}
                <div
                    ref={parentRef}
                    className="max-h-[calc(100vh-230px)] overflow-y-auto pr-1"
                >
                    {isInitialLoading && (
                        <div className="space-y-3">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div
                                    key={i}
                                    className="bg-white border border-gray-100 rounded-2xl p-4 flex gap-4 animate-pulse"
                                >
                                    <div className="h-10 w-10 bg-slate-100 rounded-full shrink-0" />
                                    <div className="flex-1 space-y-2 py-1">
                                        <div className="h-4 bg-slate-100 rounded w-3/4" />
                                        <div className="h-3 bg-slate-100 rounded w-1/4" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty state */}
                    {!isInitialLoading && notifications.length === 0 && (
                        <div className="bg-white border border-gray-100 rounded-2xl p-16 flex flex-col items-center justify-center text-center shadow-sm">
                            <div className="h-16 w-16 bg-slate-50 text-gray-400 rounded-full flex items-center justify-center mb-4">
                                <BellOff size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-1">
                                No notifications found
                            </h3>
                            <p className="text-sm text-gray-500 max-w-sm">
                                You don't have any notifications under this section right now.
                            </p>
                        </div>
                    )}

                    {/* Virtualized Container */}
                    {!isInitialLoading && notifications.length > 0 && (
                        <div
                            style={{
                                height: `${rowVirtualizer.getTotalSize()}px`,
                                width: "100%",
                                position: "relative",
                            }}
                        >
                            {virtualItems.map((virtualRow) => {
                                const isLoaderRow = virtualRow.index > notifications.length - 1;
                                const notification = notifications[virtualRow.index];

                                if (isLoaderRow) {
                                    return (
                                        <div
                                            key="loader-row"
                                            ref={rowVirtualizer.measureElement}
                                            data-index={virtualRow.index}
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: "100%",
                                                transform: `translateY(${virtualRow.start}px)`,
                                            }}
                                            className="flex justify-center items-center gap-2 py-4"
                                        >
                                            {hasMore && (
                                                <>
                                                    <span className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                                    <span className="text-xs text-gray-500 font-medium">
                                                        Loading more notifications...
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    );
                                }

                                if (!notification) return null;
                                const isUnread = !notification.seen;

                                return (
                                    <div
                                        key={notification.id}
                                        ref={rowVirtualizer.measureElement}
                                        data-index={virtualRow.index}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            transform: `translateY(${virtualRow.start}px)`,
                                        }}
                                        className="pb-3"
                                    >
                                        <div
                                            className={`group relative bg-white border rounded-2xl p-4 flex gap-4 transition-all duration-200 shadow-xs hover:shadow-md hover:border-primary/20 border-l-3
                                                ${isUnread
                                                    ? "border-l-primary bg-cyan-50/5"
                                                    : "border-gray-100 border-l-gray-300"
                                                }
                                            `}
                                        >
                                            {/* Icon */}
                                            <div
                                                className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 shadow-sm
                                                    ${isUnread
                                                        ? "bg-primary text-white"
                                                        : "bg-slate-50 text-slate-400"
                                                    }
                                                `}
                                            >
                                                <Bell size={18} />
                                            </div>

                                            {/* Content */}
                                            <div
                                                onClick={() => handleNotificationClick(notification)}
                                                className="flex-1 min-w-0 cursor-pointer"
                                            >
                                                <p
                                                    className={`text-sm leading-relaxed transition-colors hover:text-primary
                                                        ${isUnread ? "font-bold text-slate-800" : "text-slate-600 font-medium"}
                                                    `}
                                                >
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center gap-1 text-[11px] text-gray-400 mt-2 font-medium">
                                                    <Clock size={12} />
                                                    <span>{moment(notification.createdAt).fromNow()}</span>
                                                    <span className="mx-1">•</span>
                                                    <span>{formatDate(notification.createdAt)}</span>
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div
                                                onClick={(e) => e.stopPropagation()}
                                                className="flex items-center gap-2 self-center sm:opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                {isUnread && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMarkSingleAsSeen(notification.id);
                                                        }}
                                                        className="p-2 bg-slate-50 hover:bg-primary/10 text-slate-400 hover:text-primary rounded-xl transition-all cursor-pointer"
                                                        title="Mark as read"
                                                    >
                                                        <MailOpen size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSingle(notification.id, isUnread);
                                                    }}
                                                    className="p-2 bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-xl transition-all cursor-pointer"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* End of list */}
                    {!isInitialLoading && !hasMore && notifications.length > 0 && (
                        <p className="text-center text-xs text-gray-400 py-4">
                            No more notifications
                        </p>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Notification;
